import axios from "./axiosWrapper"
import { getConfig, getBaseUrl } from "@/controllers/BaseController"
import { loadConfig } from "@/configLoader"
import { Device } from "@capacitor/device"
import { Preferences } from "@capacitor/preferences"
import { Capacitor } from "@capacitor/core"

const isWebPlatform = platform => platform === "web"

async function setRedirectUrl(url) {
  const { platform } = await Device.getInfo()
  const key = "redirectAfterLogin"

  if (isWebPlatform(platform)) {
    window.localStorage.setItem(key, url)
  } else {
    await Preferences.set({ key, value: url })
  }
}

async function getRedirectUrl() {
  const { platform } = await Device.getInfo()
  const key = "redirectAfterLogin"
  if (isWebPlatform(platform)) {
    return window.localStorage.getItem(key)
  } else {
    const { value } = await Preferences.get({ key })
    return value
  }
}

async function setToken(tokenValue) {
  const platform = (await Device.getInfo()).platform
  const key = "pinestack.token"

  if (isWebPlatform(platform)) {
    window.localStorage.setItem(key, tokenValue)
  } else {
    await Preferences.set({ key, value: tokenValue })
  }
}

async function isIos() {
  const { operatingSystem } = await Device.getInfo()
  return operatingSystem === "ios"
}

async function appInstalledNative() {
  if (!Capacitor.isNativePlatform()) return false

  try {
    const { App } = await import("@capacitor/app")
    const info = await App.getInfo()
    return !!info?.name
  } catch (error) {
    console.error("Failed to check if app is installed:", error)
    return false
  }
}

export const authenticate = async (email = null, options = {}) => {
  try {
    const config = await loadConfig()
    const { platform } = await Device.getInfo()
    let isIOS = await isIos()

    const redirectAfterLogin = (await getRedirectUrl()) || "/"
    await setRedirectUrl(redirectAfterLogin)

    let callbackUri = `${window.location.origin}/oauth2/code/callback`
    const hostname = window.location.hostname.toLowerCase()

    let isAppInstalled = await appInstalledNative()
    if (isAppInstalled) {
      callbackUri = hostname === "localhost" ? config.localAppCallBackUri : config.appCallBackUri
    } else {
      callbackUri = hostname === "localhost" ? config.localCallbackUri : config.callbackUri
    }

    const lowerHost = hostname?.toLowerCase()
    if (!isAppInstalled && (lowerHost.includes("hbc.qa") || lowerHost.includes("pia.qa"))) {
      callbackUri = config?.["callBackUriQA"] || config?.["callbackUri"]
    } else if (!isAppInstalled && lowerHost.includes("pia.staging")) {
      callbackUri = config?.["callbackUriStaging"] || config?.["callbackUri"]
    }

    const browserLocale = options.locale || navigator.language.split("-")[0] || "en"
    const keycloakLocale = browserLocale === "de" ? "de" : "en"

    let authUrl = `${config.authorizationUri}?client_id=${encodeURIComponent(config.clientId)}&redirect_uri=${callbackUri}&response_type=token&scope=email profile`

    if (email) {
      authUrl += `&login_hint=${encodeURIComponent(email)}`
    }

    if (options.skipLogin) {
      authUrl += `&prompt=create`
    }

    authUrl += `&kc_locale=${keycloakLocale}`

    if (isWebPlatform(platform) && !isIOS) {
      window.open(authUrl, "_self")
    } else if (isIOS) {
      const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true
      const isInChrome = /CriOS/.test(navigator.userAgent)

      if (isInChrome && !isStandalone) {
        window.open(authUrl, "_self")
      } else {
        const { InAppBrowser } = await import("@capgo/capacitor-inappbrowser")
        const options = {
          url: authUrl,
          showArrow: false,
          title: "Login"
        }

        await InAppBrowser.openWebView(options)
        setInterval(() => {
          InAppBrowser.executeScript({
            code: `
              try {
                const url = window.location.href;
                const style = document.createElement('style');
                style.innerHTML = '.member-to-be { display: none !important; }';
                document.head.appendChild(style);
                if (url === 'https://members.factory.network/dashboard') {
                  window.mobileApp?.close?.();
                }
              } catch (e) {
                console.log('Injected script error:', e);
              }
            `
          })
        }, 10)

        InAppBrowser.addListener("urlChangeEvent", async info => {
          const url = info.url
          if (url.includes("access_token")) {
            try {
              InAppBrowser.close()
              const tokenValue = url.split("access_token=")[1].split("&")[0]
              await setToken(tokenValue)
              const redirectAfterLogin = (await getRedirectUrl()) || "/"
              await setRedirectUrl("")
              window.location.href = redirectAfterLogin
            } catch (e) {
              console.error("Token handling failed:", e)
            }
            return
          }
          if (url === "https://members.factory.network/dashboard" || url.includes("operations.hammerbrooklyn.pinestack.eu/4a289bb3")) {
            InAppBrowser.close()
          }
        })
      }
    } else {
      const { InAppBrowser } = await import("@capgo/capacitor-inappbrowser")
      const options = {
        url: authUrl,
        closeButtonColor: "#ffffff",
        title: "Login",
        activeNativeNavigationForWebview: true,
        disableGoBackOnNativeApplication: false,
        isPresentAfterPageLoad: false,
        isAnimated: false,
        showReloadButton: false,
        visibleTitle: false,
        toolbarColor: "white",
        showArrow: false
      }

      await InAppBrowser.openWebView(options)
      InAppBrowser.addListener("urlChangeEvent", async info => {
        if (
          info.url.includes("access_token") ||
          info.url === "https://members.factory.network/dashboard" ||
          info.url?.includes("operations.hammerbrooklyn.pinestack.eu/4a289bb3")
        ) {
          InAppBrowser.close()
          const tokenValue = info.url.split("access_token=")[1].split("&")[0]
          await setToken(tokenValue)
          const redirectAfterLogin = (await getRedirectUrl()) || "/"
          await setRedirectUrl("")
          window.location.href = redirectAfterLogin
        }
      })
    }
  } catch (error) {
    console.error(error)
  }
}

export const getDemoSignups = async () => {
  const config = await getConfig()
  config.url = `${await getBaseUrl()}contacts`
  const response = await axios(config)
  return response.data.items || response.data || []
}

export const createDemoSignup = async payload => {
  const config = await getConfig()
  config.url = `${await getBaseUrl()}contacts`
  config.method = "POST"
  delete config.headers.Authorization
  config.data = {
    name: payload.name?.trim() || null,
    email: payload.email?.trim().toLowerCase() || null,
    company: payload.company?.trim() || null,
    phone: payload.phone?.trim() || null,
    message: payload.message?.trim() || null
  }
  const response = await axios(config)
  return response.data
}

export const updateDemoSignup = async (id, payload) => {
  const config = await getConfig()
  config.url = `${await getBaseUrl()}contacts/${id}`
  config.method = "PUT"
  config.data = {
    id,
    name: payload.name?.trim() || null,
    email: payload.email?.trim().toLowerCase() || null,
    company: payload.company?.trim() || null,
    phone: payload.phone?.trim() || null,
    message: payload.message?.trim() || null
  }
  const response = await axios(config)
  return response.data
}

export const deleteDemoSignup = async id => {
  const config = await getConfig()
  config.url = `${await getBaseUrl()}contacts/${id}`
  config.method = "DELETE"
  const response = await axios(config)
  return response.data
}
